import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { tokenStorage } from './tokenStorage';
import { isZitadelEnabled } from './zitadelMode';
import { getZitadelClient } from './zitadelClient';
import { createDebugLogger } from '../utils/debug';

const dlog = createDebugLogger('auth');

const AuthContext = createContext(null);

// Synchroner Boot-Read — vermeidet setState-in-useEffect-Pattern.
// Läuft genau einmal pro Tab beim Modul-Load.
const bootTokens = (() => {
  if (typeof window === 'undefined') {
    return { accessToken: null, refreshToken: null };
  }
  return tokenStorage.read();
})();

const ZITADEL_MODE = (() => {
  try {
    return isZitadelEnabled();
  } catch {
    return false;
  }
})();

/**
 * Auth-State-Machine: 'anonymous' | 'authenticated' | 'mfa-pending'
 * Tokens leben in Refs (kein Re-Render auf Refresh).
 *
 * Im Zitadel-Mode (VITE_ZITADEL_ENABLED=1):
 *  - Login/Refresh/Logout passiert über oidc-client-ts gegen Zitadel
 *  - mfa-pending wird nie erreicht (Zitadel macht MFA in seiner Hosted-UI)
 *  - Tokens werden zusätzlich in tokenStorage gespiegelt damit API-Client
 *    sie synchron lesen kann (oidc-client-ts ist async)
 *
 * Legacy-Mode: unverändert — Email/Password gegen Core, MFA via Core.
 */
export function AuthProvider({ children }) {
  const accessRef = useRef(bootTokens.accessToken);
  const refreshRef = useRef(bootTokens.refreshToken);

  const [status, setStatus] = useState(
    bootTokens.accessToken ? 'authenticated' : 'anonymous',
  );
  const [mfaChallenge, setMfaChallenge] = useState(null);

  // Cross-Tab-Sync via storage-Event (Legacy) bzw. BroadcastChannel (Zitadel)
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== tokenStorage.keys.access) return;
      const t = tokenStorage.read();
      accessRef.current = t.accessToken;
      refreshRef.current = t.refreshToken;
      setStatus(t.accessToken ? 'authenticated' : 'anonymous');
    };
    window.addEventListener('storage', handler);
    // Zitadel-Mode: BroadcastChannel für token_rotated (siehe Spec §3)
    let bc = null;
    if (ZITADEL_MODE && 'BroadcastChannel' in window) {
      bc = new BroadcastChannel('orbit_auth');
      bc.onmessage = (msg) => {
        if (msg.data?.type === 'token_rotated') {
          const t = tokenStorage.read();
          accessRef.current = t.accessToken;
          refreshRef.current = t.refreshToken;
          setStatus(t.accessToken ? 'authenticated' : 'anonymous');
        } else if (msg.data?.type === 'signed_out') {
          accessRef.current = null;
          refreshRef.current = null;
          setStatus('anonymous');
        }
      };
    }
    return () => {
      window.removeEventListener('storage', handler);
      if (bc) bc.close();
    };
  }, []);

  const broadcastAuth = useCallback((type) => {
    if (!ZITADEL_MODE || typeof BroadcastChannel === 'undefined') return;
    try {
      const bc = new BroadcastChannel('orbit_auth');
      bc.postMessage({ type });
      bc.close();
    } catch {
      /* best-effort */
    }
  }, []);

  const setTokens = useCallback((t) => {
    accessRef.current = t.accessToken;
    refreshRef.current = t.refreshToken;
    tokenStorage.write(t);
    setMfaChallenge(null);
    setStatus('authenticated');
    broadcastAuth('token_rotated');
  }, [broadcastAuth]);

  const beginMfa = useCallback((challenge) => {
    setMfaChallenge(challenge);
    setStatus('mfa-pending');
  }, []);

  const signOut = useCallback(async (_reason) => {
    accessRef.current = null;
    refreshRef.current = null;
    tokenStorage.clear();
    setMfaChallenge(null);
    setStatus('anonymous');
    broadcastAuth('signed_out');
    if (ZITADEL_MODE) {
      try {
        const mgr = getZitadelClient();
        const user = await mgr.getUser();
        if (user) {
          await mgr.signoutRedirect({ id_token_hint: user.id_token });
          return;
        }
        await mgr.removeUser();
      } catch (err) {
        dlog('zitadel-logout failed, falling back to local clear', err);
      }
    }
  }, [broadcastAuth]);

  /**
   * Im Zitadel-Mode triggert das Login einen Redirect zur Hosted-Login-UI.
   * Im Legacy-Mode ist das die Email-Password-Action aus pages/Login.jsx.
   */
  const beginZitadelLogin = useCallback(async (returnTo) => {
    if (!ZITADEL_MODE) throw new Error('zitadel_disabled');
    const mgr = getZitadelClient();
    await mgr.signinRedirect({ state: returnTo ?? '/' });
  }, []);

  const value = useMemo(
    () => ({
      mode: ZITADEL_MODE ? 'zitadel' : 'legacy',
      status,
      mfaChallenge,
      getAccessToken: () => accessRef.current,
      getRefreshToken: () => refreshRef.current,
      setTokens,
      beginMfa,
      signOut,
      beginZitadelLogin,
    }),
    [status, mfaChallenge, setTokens, beginMfa, signOut, beginZitadelLogin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
